import React from 'react';
import { Text } from '@mantine/core';

import { ActiveFilter, FilterConfig } from '../../types';
import { BooleanFilterForModal, CheckboxFilterForModal, DateFilterForModal, FetchableCheckboxFilterForModal, NumberFilterForModal } from '../filter-components-by-types';
import { NumberFilter } from '../../FilterTypeRenderers';

export interface SelectedFilterContentProps {
  selectedCategory: string;
  selectedChildFilter: string | null;
  selectedCategoryType?: 'idea' | 'evidence' | null;
  filtersConfig: { idea?: Record<string, FilterConfig>; evidence?: Record<string, FilterConfig> };
  localActiveFilters: ActiveFilter[];
  setLocalActiveFilters: (filters: ActiveFilter[] | ((prev: ActiveFilter[]) => ActiveFilter[])) => void;
  getFilterKey: (filterObject: 'idea' | 'evidence', filterName: string) => string;
  setFetchableCheckboxCounts: React.Dispatch<React.SetStateAction<{ [key: string]: number }>>;
  clearTrigger: number;
}

const SelectedFilterContent: React.FC<SelectedFilterContentProps> = ({
  selectedCategory,
  selectedChildFilter,
  selectedCategoryType,
  filtersConfig,
  localActiveFilters,
  setLocalActiveFilters,
  getFilterKey,
  setFetchableCheckboxCounts,
  clearTrigger,
}) => {
  if (!selectedCategory || !filtersConfig) {
    return (
      <Text size="sm" c="#9ca3af" ta="center" mt={48} mx="md" style={{ lineHeight: 1.6 }}>
        Select a filter category from the list to see options
      </Text>
    );
  }

  let filterConfig: FilterConfig | undefined;
  let filterName: string | null = selectedChildFilter ?? null;
  let actualFilterObject: 'idea' | 'evidence';

  // Use selectedCategoryType to determine which config to check first
  // This ensures we use the correct filterObject based on which section the user clicked
  if (selectedCategoryType === 'evidence') {
    // Check evidence config first when user selected from evidence section
    filterConfig = filtersConfig.evidence?.[selectedCategory];
    actualFilterObject = 'evidence';

    if (filterConfig) {
      if (filterConfig.childFilters && selectedChildFilter && selectedChildFilter !== selectedCategory) {
        filterConfig = filterConfig.childFilters[selectedChildFilter];
        filterName = selectedChildFilter;
      } else if (filterConfig.childFilters && !selectedChildFilter) {
        if (filterConfig.type === 'fetchableCheckbox') {
          filterName = selectedCategory;
        } else {
          return null;
        }
      } else {
        filterName = selectedCategory;
      }
    } else {
      // Fallback to idea config if not found in evidence
      filterConfig = filtersConfig.idea?.[selectedCategory];
      actualFilterObject = 'idea';
      if (filterConfig) {
        if (filterConfig.childFilters && selectedChildFilter && selectedChildFilter !== selectedCategory) {
          filterConfig = filterConfig.childFilters[selectedChildFilter];
          filterName = selectedChildFilter;
        } else if (filterConfig.childFilters && !selectedChildFilter) {
          if (filterConfig.type === 'fetchableCheckbox') {
            filterName = selectedCategory;
          } else {
            return null;
          }
        } else {
          filterName = selectedCategory;
        }
      }
    }
  } else {
    // Default behavior: check idea config first (for idea section or when categoryType not specified)
    filterConfig = filtersConfig.idea?.[selectedCategory];
    actualFilterObject = 'idea';

    if (filterConfig) {
      if (filterConfig.childFilters && selectedChildFilter && selectedChildFilter !== selectedCategory) {
        filterConfig = filterConfig.childFilters[selectedChildFilter];
        filterName = selectedChildFilter;
      } else if (filterConfig.childFilters && !selectedChildFilter) {
        if (filterConfig.type === 'fetchableCheckbox') {
          filterName = selectedCategory;
        } else {
          return null;
        }
      } else {
        filterName = selectedCategory;
      }
    } else {
      actualFilterObject = 'evidence';
      const categoryData = filtersConfig.evidence?.[selectedCategory];

      if (categoryData?.childFilters && selectedChildFilter && selectedChildFilter !== selectedCategory) {
        filterConfig = categoryData.childFilters[selectedChildFilter];
        filterName = selectedChildFilter;
      } else if (categoryData?.childFilters && !selectedChildFilter) {
        if (categoryData.type === 'fetchableCheckbox') {
          filterName = selectedCategory;
        } else {
          return null;
        }
      } else {
        filterConfig = categoryData;
        filterName = selectedCategory;
      }
    }
  }

  if (!filterConfig || !filterName) return null;

  const filterConfigWithObject: FilterConfig = {
    ...filterConfig,
    filterObject: filterConfig.filterObject || actualFilterObject,
  };

  const resolvedFilterName = filterName;
  const filterProps = {
    filterConfig: filterConfigWithObject,
    filterName: resolvedFilterName,
    selectedCategory,
    localActiveFilters,
    setLocalActiveFilters,
    filtersConfig,
    onCountUpdate: (count: number) => {
      const key = getFilterKey(actualFilterObject, resolvedFilterName);
      setFetchableCheckboxCounts(prev => ({ ...prev, [key]: count }));
    },
    clearTrigger,
  };

  switch (filterConfig.type) {
  case 'checkbox':
  case 'select':
    return <CheckboxFilterForModal {...filterProps} />;

  case 'fetchableCheckbox':
    return <FetchableCheckboxFilterForModal {...filterProps} />;

  case 'date':
  case 'date-range':
    return <DateFilterForModal {...filterProps} />;

  case 'number':
    return <NumberFilterForModal {...filterProps} />;

  case 'boolean':
  case 'userIdIn':
    return <BooleanFilterForModal {...filterProps} />;

  case 'parent':
    return (
      <Text size="sm" c="dimmed" ta="center" mt="xl">
        Select a filter option from the middle panel
      </Text>
    );

  default:
    return (
      <Text size="sm" c="dimmed">
        Unsupported filter type: {filterConfig.type}
      </Text>
    );
  }
};

export default SelectedFilterContent;
